import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { PhoneCall, Mic, Globe, Clock, CheckCircle, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const SHREYA = {
  name: "Shreya",
  role: "AI Calling Agent",
  language: "Hindi / English",
  voice: "Female · Natural",
  purpose: "Outbound lead qualification — calls new leads within minutes of submission, captures service interest, and schedules callbacks.",
  capabilities: [
    "Introduces Losung360 services (ShipMaxx, ShipBulk)",
    "Qualifies lead interest in FTL, PTL & shipping",
    "Handles Hindi and English conversations",
    "Logs transcript and outcome back to LMS",
    "Triggers callback scheduling on interest",
  ],
  availability: "24 × 7 · Auto-triggered on new lead",
  avgCallDuration: "~2–3 min",
  callerId: "+91-XXXXXXXXXX",
};

export default function AdminAgent() {
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const { data } = await api.get("/ai-calls/config");
        setActive(data.enabled === true);
      } catch {
        // silently ignore — default to inactive
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleToggle = () => setConfirmOpen(true);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await api.post("/ai-calls/trigger", { enabled: active ? 0 : 1 });
      setActive((prev) => !prev);
      toast.success(active ? "Shreya deactivated." : "Shreya activated successfully.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update agent status.");
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">AI Agents</h1>
          <p className="text-sm text-slate-500 mt-1">Manage automated calling agents for lead outreach.</p>
        </div>

        {/* Shreya Card */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          {/* Card header */}
          <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-xl font-bold shadow-sm">
                S
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-slate-900">{SHREYA.name}</span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                    active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-slate-400"}`} />
                    {active ? "Active" : "Inactive"}
                  </span>
                </div>
                <p className="text-sm text-slate-500 mt-0.5">{SHREYA.role}</p>
              </div>
            </div>

            {configLoading ? (
              <div className="px-5 py-2 text-sm text-slate-400">Loading…</div>
            ) : active ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate("/admin/agent/call-history")}
                  className="px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
                >
                  <PhoneCall size={14} /> Call History
                </button>
                <button
                  onClick={handleToggle}
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
                >
                  Deactivate
                </button>
              </div>
            ) : (
              <button
                onClick={handleToggle}
                className="px-5 py-2 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 shadow-sm"
              >
                Activate
              </button>
            )}
          </div>

          {/* Details grid */}
          <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Purpose</p>
              <p className="text-sm text-slate-700 leading-relaxed">{SHREYA.purpose}</p>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Capabilities</p>
              <ul className="space-y-1.5">
                {SHREYA.capabilities.map((c) => (
                  <li key={c} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle size={14} className="text-orange-500 mt-0.5 shrink-0" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Footer stats */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-wrap gap-6">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Globe size={14} className="text-slate-400" />
              <span>{SHREYA.language}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Mic size={14} className="text-slate-400" />
              <span>{SHREYA.voice}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Clock size={14} className="text-slate-400" />
              <span>{SHREYA.availability}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <PhoneCall size={14} className="text-slate-400" />
              <span>Avg duration: {SHREYA.avgCallDuration}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation dialog */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">
                {active ? "Deactivate Shreya?" : "Activate Shreya?"}
              </h3>
              <button onClick={() => setConfirmOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400">
                <X size={16} />
              </button>
            </div>
            <div className="px-6 py-4 text-sm text-slate-600">
              {active
                ? "Shreya will stop making outbound calls. Leads will no longer be auto-called on submission."
                : "Shreya will begin making outbound calls to new leads automatically after submission. Confirm to proceed."}
            </div>
            <div className="px-6 py-4 flex justify-end gap-2 border-t border-slate-100">
              <button
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading}
                className={`px-4 py-2 text-sm font-semibold rounded-lg text-white disabled:opacity-60 ${
                  active ? "bg-red-500 hover:bg-red-600" : "bg-orange-500 hover:bg-orange-600"
                }`}
              >
                {loading ? "Please wait…" : active ? "Yes, Deactivate" : "Yes, Activate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
